import { useCallback, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useQueryScope } from "@/hooks/use-query-scope";
import { useScopedQuery } from "@/hooks/use-scoped-query";
import { track, type AnalyticsSource } from "@/lib/analytics";
import { isConnected } from "@/lib/integrations/msteams-link";
import { userTeamsLinksKey, userTeamsLinksQuery } from "@/queries/msteams";
import { GLOBAL_TENANT } from "@/queries/scope";
import { useStore } from "@/stores/store-context";
import type { TTeamsDigestSendResult, TTeamsWorkspaceLink } from "@/types/msteams-link";

const NO_ROWS: TTeamsWorkspaceLink[] = [];

export interface UserTeamsLinksRead {
  /** One row per workspace of theirs with Teams, in the server's order (by name).
   *  Empty before the first read, and when it failed: neither may render as an offer. */
  readonly workspaces: TTeamsWorkspaceLink[];
  /** Whether this person has anywhere to link at all: the gate on rendering the block. */
  readonly hasConnectedWorkspaces: boolean;
  /** The workspaces they are {@link isConnected connected} in. */
  readonly connectedWorkspaces: TTeamsWorkspaceLink[];
  /** The connected ones that are not the workspace they are looking at (NG-1622); empty
   *  where there is no current workspace to be other than. */
  readonly otherConnectedWorkspaces: TTeamsWorkspaceLink[];
  /** Whether this person's own Microsoft identity is bound anywhere (NG-1187): the gate on
   *  the per-category Teams switches. Matched is enough; installed is not required. */
  readonly hasMatchedLink: boolean;
  /** Where the channel is live and they are not connected, so their colleagues get Teams
   *  messages and they do not (OQ2): what the bell nudges about. */
  readonly workspacesWorthNudging: TTeamsWorkspaceLink[];
  /** Read them again now: the block's mount, and the sign-in callback's return. */
  readonly refresh: () => Promise<void>;
}

/**
 * The signed-in person's own Microsoft Teams links, across every workspace they are in
 * (NG-2813; plan §5a FR5–FR7, NG-1134). Read while `enabled` (the Teams flag), once:
 * the profile block, the notification settings' column and the bell share one read, and
 * a surface that wants it fresh calls `refresh`.
 *
 * Must be called from an `observer` component.
 */
export function useUserTeamsLinks({ enabled }: { enabled: boolean }): UserTeamsLinksRead {
  const { msTeamsLinkService, workspace } = useStore();
  const client = useQueryClient();
  const { mode, query } = useScopedQuery(
    GLOBAL_TENANT,
    (scope) => userTeamsLinksQuery(msTeamsLinkService, scope),
    { flag: null, enabled },
  );
  const scope = useQueryScope(GLOBAL_TENANT);
  const principal = scope?.principal;
  const workspaces = mode === "cache" ? (query.data?.workspaces ?? NO_ROWS) : NO_ROWS;
  const currentId = workspace.currentWorkspace?.id;

  const refresh = useCallback(async () => {
    if (principal === undefined) return;
    // Joins a read already in flight rather than restarting it.
    await client.fetchQuery({
      ...userTeamsLinksQuery(msTeamsLinkService, { tenant: GLOBAL_TENANT, principal }),
      staleTime: 0,
    });
  }, [client, msTeamsLinkService, principal]);

  return useMemo(() => {
    const connectedWorkspaces = workspaces.filter(isConnected);
    return {
      workspaces,
      hasConnectedWorkspaces: workspaces.length > 0,
      connectedWorkspaces,
      otherConnectedWorkspaces:
        currentId === undefined
          ? NO_ROWS
          : connectedWorkspaces.filter((row) => row.workspace.id !== currentId),
      hasMatchedLink: connectedWorkspaces.length > 0,
      workspacesWorthNudging: workspaces.filter(
        (row) => row.connection_state === "enabled" && !isConnected(row),
      ),
      refresh,
    };
  }, [workspaces, currentId, refresh]);
}

/** Which of this person's round trips is in flight (NG-1134; `send_digest` is NG-1641). */
type TTeamsLinkPendingAction = "signin" | "unlink" | "send_digest" | null;

export interface UserTeamsLinkWriters {
  readonly pendingAction: TTeamsLinkPendingAction;
  /** Mint the Microsoft authorization URL for this person's link; the caller navigates. */
  startSignIn(returnPath: string, source?: AnalyticsSource): Promise<string>;
  /** Opt out everywhere (FR7). */
  unlink(source?: AnalyticsSource): Promise<void>;
  /** Send their Monday summary now, for one workspace (NG-1641). Rethrows every refusal. */
  sendDigestNow(workspaceSlug: string, source?: AnalyticsSource): Promise<TTeamsDigestSendResult>;
}

/**
 * The person's own Teams link writers (contract §3, NG-2813). Each logs its own
 * failure and emits its event only once the server has it.
 *
 * **Both link writes wait for the server**: `startSignIn` has no local effect (it mints
 * a URL and the caller navigates away), and `unlink` is destructive (every link becomes
 * `unlinked`, pending deliveries are dropped), and answers with the whole payload,
 * which replaces the cached links. `sendDigestNow` changes no link, so it writes
 * nothing; it owns only `pendingAction`. Nothing here touches `window`.
 *
 * Reads the signed-in user from the user store, so the calling component must be an
 * `observer`.
 */
export function useUserTeamsLinkWriters(): UserTeamsLinkWriters {
  const { msTeamsLinkService: service } = useStore();
  const client = useQueryClient();
  const scope = useQueryScope(GLOBAL_TENANT);
  const principal = scope?.principal;
  const [pendingAction, setPendingAction] = useState<TTeamsLinkPendingAction>(null);

  const startSignIn = useCallback<UserTeamsLinkWriters["startSignIn"]>(
    async (returnPath, source = "page") => {
      setPendingAction("signin");
      try {
        const { authorization_url } = await service.startSignIn(returnPath);
        track("teams_link_signin_started", { source });
        return authorization_url;
      } catch (error) {
        console.error("[msteams] failed to start my Microsoft sign-in", { returnPath, error });
        throw error;
      } finally {
        setPendingAction(null);
      }
    },
    [service],
  );

  const unlink = useCallback<UserTeamsLinkWriters["unlink"]>(
    async (source = "page") => {
      setPendingAction("unlink");
      try {
        const payload = await service.unlink();
        if (principal !== undefined) {
          client.setQueryData(userTeamsLinksKey({ tenant: GLOBAL_TENANT, principal }), payload);
        }
        track("teams_link_removed", { source });
      } catch (error) {
        console.error("[msteams] failed to unlink my Teams account", { error });
        throw error;
      } finally {
        setPendingAction(null);
      }
    },
    [service, client, principal],
  );

  const sendDigestNow = useCallback<UserTeamsLinkWriters["sendDigestNow"]>(
    async (workspaceSlug, source = "page") => {
      setPendingAction("send_digest");
      try {
        const result = await service.sendDigestNow(workspaceSlug);
        track("teams_digest_sent", { sent: result.sent, source });
        return result;
      } catch (error) {
        console.error("[msteams] failed to send my Monday summary now", { workspaceSlug, error });
        throw error;
      } finally {
        setPendingAction(null);
      }
    },
    [service],
  );

  return { pendingAction, startSignIn, unlink, sendDigestNow };
}
